import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { apiCustomers } from '../../lib/api';
import { toast } from 'sonner';

const empty = {
  name: '', firstName: '', lastName: '', company: '', email: '', phone: '',
  type: 'Residential', status: 'Active', address: '', city: '', state: 'CA', zip: '',
  accountNumber: '', balance: 0, notes: '',
};

export default function CustomerFormDialog({ open, setOpen, editing, onSaved }) {
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) setF({ ...empty, ...editing });
    else setF(empty);
  }, [editing, open]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!f.name.trim()) { toast.error('Customer name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await apiCustomers.update(editing.id, { ...f, balance: Number(f.balance) || 0 });
        toast.success('Customer updated');
      } else {
        const id = `C-${Date.now().toString(36)}`;
        await apiCustomers.create({ ...f, id, balance: Number(f.balance) || 0 });
        toast.success('Customer added');
      }
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl" data-testid="customer-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Customer' : 'New Customer'}</DialogTitle>
          <DialogDescription>{editing ? 'Update this customer record.' : 'Add a customer to your account.'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Name / Account Name</Label>
            <Input data-testid="customer-name-input" value={f.name} onChange={(e) => set('name', e.target.value)} className="mt-1.5" placeholder="e.g. Smith Residence" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Company</Label><Input value={f.company} onChange={(e) => set('company', e.target.value)} className="mt-1.5" /></div>
            <div><Label>Account #</Label><Input value={f.accountNumber} onChange={(e) => set('accountNumber', e.target.value)} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input data-testid="customer-email-input" value={f.email} onChange={(e) => set('email', e.target.value)} className="mt-1.5" /></div>
            <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => set('phone', e.target.value)} className="mt-1.5" /></div>
          </div>
          <div><Label>Address</Label><Input value={f.address} onChange={(e) => set('address', e.target.value)} className="mt-1.5" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>City</Label><Input value={f.city} onChange={(e) => set('city', e.target.value)} className="mt-1.5" /></div>
            <div><Label>State</Label><Input value={f.state} onChange={(e) => set('state', e.target.value)} className="mt-1.5" /></div>
            <div><Label>Zip</Label><Input value={f.zip} onChange={(e) => set('zip', e.target.value)} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={f.type} onValueChange={(v) => set('type', v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Residential">Residential</SelectItem>
                  <SelectItem value="Commercial">Commercial</SelectItem>
                  <SelectItem value="Multi-Unit">Multi-Unit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="customer-save-btn" onClick={submit} disabled={saving} className="bg-[#8BC53F] hover:bg-[#6FA82C]">
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Customer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
